"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";

/** Observer silent past this window ⇒ assume it's broken and reveal (fail-open). */
const OBSERVER_SILENT_TIMEOUT_MS = 2000;

type Props = {
  children: ReactNode;
  className?: string;
};

/**
 * Fade-in-up on first viewport entry: 200ms ease-out, once-only (Brand §7).
 *
 * Fail-open by design: content must never stay hidden because the observer
 * missed. Reveals immediately when the element is already in view on mount,
 * when reduced motion is requested, or when the observer stays silent
 * (prerender, background tab, missed callback after route transitions).
 *
 * `SITE-FIX01`: the server HTML and the first client render are visible. Only
 * the effect, which runs once JavaScript has, hides a block that is still
 * below the viewport, so a visitor without JavaScript sees every block
 * (`docs/03` §7).
 */
export function RevealOnView({ children, className }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (
      typeof IntersectionObserver === "undefined" ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }

    // Already within the viewport on mount (restored scroll position,
    // client-side navigation landing mid-page): no entry animation.
    const rect = el.getBoundingClientRect();
    if (rect.top < window.innerHeight && rect.bottom > 0) {
      return;
    }

    setHidden(true);

    // A healthy observer always delivers an initial callback at observe time
    // (isIntersecting=false when off-screen). Zero callbacks ⇒ broken state.
    let observerAlive = false;

    const observer = new IntersectionObserver(
      ([entry]) => {
        observerAlive = true;
        if (!entry?.isIntersecting) return;
        setHidden(false);
        observer.disconnect();
      },
      // threshold 0 (any visible pixel): a fractional threshold can never be
      // met by section bands taller than ~1/threshold × the viewport.
      { root: null, rootMargin: "0px 0px -8% 0px", threshold: 0 },
    );
    observer.observe(el);

    const failOpen = window.setTimeout(() => {
      if (!observerAlive) setHidden(false);
    }, OBSERVER_SILENT_TIMEOUT_MS);

    return () => {
      observer.disconnect();
      window.clearTimeout(failOpen);
    };
  }, []);

  return (
    <div
      ref={ref}
      className={cn(
        "transition-[opacity,transform] duration-200 ease-out will-change-[opacity,transform]",
        hidden ? "translate-y-2 opacity-0" : "translate-y-0 opacity-100",
        className,
      )}
    >
      {children}
    </div>
  );
}
